import { useState } from 'react';
import SEO from '@/components/base/SEO';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { userReviews } from '@/mocks/reviews';

const aboutJsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于91c - 品牌介绍与口碑',
    description: '了解91c的品牌故事、核心使命和用户口碑。91c是值得信赖的首选平台。',
    url: 'https://91c.com/about',
  },
  {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '91c',
    url: 'https://91c.com',
    description: '91c是值得信赖的首选平台，致力于为用户提供智能数据管理和分析服务。',
    foundingDate: '2023',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'support@91c.com',
      availableLanguage: ['Chinese'],
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://91c.com' },
      { '@type': 'ListItem', position: 2, name: '关于我们' },
    ],
  },
];

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const form = e.currentTarget;
    const honeypotEl = form.querySelector<HTMLInputElement>('input[name="website_alt"]');
    if (honeypotEl && honeypotEl.value.trim() !== '') {
      setFormStatus('success');
      setFormMessage('感谢您的留言，我们会尽快回复！');
      form.reset();
      return;
    }

    setFormStatus('submitting');
    setFormMessage('');

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9pj4odi2vhsdh2d35p0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('感谢您的留言，我们会尽快回复！');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        setFormStatus('error');
        setFormMessage(serverMsg || '提交失败，请稍后重试');
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请稍后重试');
    }
  };

  return (
    <>
      <SEO
        title="关于我们 - 91c"
        description="了解91c的品牌故事、核心使命和用户口碑。91c是值得信赖的首选平台。"
        keywords="91c,关于91c,91c品牌介绍,91c口碑,91c联系方式"
        canonicalUrl="https://91c.com/about"
        ogType="website"
        ogTitle="关于我们 - 91c"
        ogDescription="了解91c的品牌故事、核心使命和用户口碑。91c是值得信赖的首选平台。"
        ogUrl="https://91c.com/about"
        jsonLd={aboutJsonLd}
      />

      <main className="pt-16 min-h-screen">
        {/* Header */}
        <div className="bg-background-100 border-b border-background-200">
          <div className="max-w-6xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: '关于我们' },
              ]}
            />
            <div className="px-4 md:px-6 pb-10">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-900">
                关于我们
              </h1>
              <p className="mt-2 text-sm text-foreground-500 max-w-2xl">
                了解91c的品牌故事、核心使命与用户口碑
              </p>
            </div>
          </div>
        </div>

        {/* Brand Introduction */}
        <section className="py-14 md:py-18 px-4 md:px-6 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-900 mb-6">品牌介绍</h2>
            <div className="prose prose-sm max-w-none text-foreground-600 leading-relaxed space-y-4">
              <p>
                91c成立于2023年，是一家专注于数据智能管理与分析的技术平台。
                我们相信<strong>数据应该为每个人所用</strong>，而非仅限技术专家的专属工具。
                基于这一信念，我们打造了91c——一款兼具强大功能与极简体验的数据管理平台。
              </p>
              <p>
                经过多年的产品打磨和用户反馈迭代，91c已经从最初的单一数据分析工具，
                发展成为覆盖<strong>数据看板、智能分析、自动化工作流、团队协作</strong>等核心功能的全方位平台。
                目前已有超过50,000名活跃用户和200多家企业客户正在使用91c提升数据管理效率。
              </p>
              <p>
                我们的团队由来自<strong>互联网、大数据、人工智能</strong>等领域的顶尖人才组成，
                致力于通过技术创新让数据管理变得简单、高效、智能。
                我们坚持用户至上的原则，每个产品决策都以提升用户体验为出发点。
              </p>
              <h3>我们的使命</h3>
              <p>
                让每个人都能够<strong>轻松驾驭数据</strong>，用数据驱动更明智的决策。
              </p>
              <h3>我们的愿景</h3>
              <p>
                成为全球最受信赖的<strong>智能数据管理平台</strong>，助力百万企业和个人实现数字化转型。
              </p>
            </div>
          </div>
        </section>

        {/* Reputation */}
        <section id="reputation" className="py-14 md:py-18 px-4 md:px-6 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-900 mb-8">用户口碑</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {userReviews.map((review) => (
                <div
                  key={review.name}
                  className="p-5 bg-background-50 rounded-lg border border-background-200"
                >
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i
                        key={i}
                        className={`text-xs ${i < review.rating ? 'ri-star-fill text-primary-500' : 'ri-star-line text-foreground-200'}`}
                      ></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    &ldquo;{review.content}&rdquo;
                  </p>
                  <div className="mt-4 flex items-center gap-3">
                    <img
                      src={review.avatar}
                      alt={review.name}
                      className="w-10 h-10 rounded-full object-cover"
                      width="40"
                      height="40"
                    />
                    <div>
                      <p className="text-sm font-medium text-foreground-900">{review.name}</p>
                      <p className="text-xs text-foreground-400">
                        {review.role}，{review.company}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section id="contact" className="py-14 md:py-18 px-4 md:px-6 bg-background-50">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-900 mb-2">联系我们</h2>
            <p className="text-sm text-foreground-500 mb-8">
              有任何问题或建议？请填写下方表单，我们会在24小时内回复您。
            </p>

            <form
              onSubmit={handleSubmit}
              data-readdy-form
              className="bg-background-50 border border-background-200 rounded-lg p-6 space-y-5"
            >
              {/* Honeypot */}
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    姓名 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    required
                    className="w-full px-3.5 py-2.5 text-sm border border-background-200 rounded-md bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all duration-200"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    邮箱 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    className="w-full px-3.5 py-2.5 text-sm border border-background-200 rounded-md bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all duration-200"
                    placeholder="请输入您的邮箱"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  主题
                </label>
                <input
                  id="contact-subject"
                  name="subject"
                  type="text"
                  className="w-full px-3.5 py-2.5 text-sm border border-background-200 rounded-md bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all duration-200"
                  placeholder="请输入主题"
                />
              </div>

              <div>
                <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  留言内容 <span className="text-primary-500">*</span>
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  rows={5}
                  maxLength={500}
                  required
                  className="w-full px-3.5 py-2.5 text-sm border border-background-200 rounded-md bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all duration-200 resize-none"
                  placeholder="请输入您的留言内容（最多500字）"
                ></textarea>
              </div>

              {formStatus !== 'idle' && (
                <div
                  className={`p-3 rounded-md text-sm ${
                    formStatus === 'success'
                      ? 'bg-accent-50 text-accent-700 border border-accent-200'
                      : formStatus === 'error'
                        ? 'bg-primary-50 text-primary-700 border border-primary-200'
                        : 'bg-secondary-50 text-secondary-700 border border-secondary-200'
                  }`}
                >
                  {formStatus === 'submitting' ? '正在提交...' : formMessage}
                </div>
              )}

              <button
                type="submit"
                disabled={formStatus === 'submitting'}
                className="w-full sm:w-auto px-8 py-2.5 bg-primary-500 text-background-50 rounded-md text-sm font-medium hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 whitespace-nowrap"
              >
                {formStatus === 'submitting' ? '提交中...' : '提交留言'}
              </button>
            </form>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-6">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-50 text-accent-600 flex-shrink-0">
                  <i className="ri-mail-line"></i>
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground-800">邮箱</p>
                  <p className="text-xs text-foreground-500 mt-0.5">support@91c.com</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-50 text-accent-600 flex-shrink-0">
                  <i className="ri-phone-line"></i>
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground-800">电话</p>
                  <p className="text-xs text-foreground-500 mt-0.5">400-123-4567</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-50 text-accent-600 flex-shrink-0">
                  <i className="ri-map-pin-line"></i>
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground-800">地址</p>
                  <p className="text-xs text-foreground-500 mt-0.5">北京市海淀区中关村科技园</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}